import { FunctionComponent, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { faLock } from "@fortawesome/pro-regular-svg-icons";

import { TableColumn } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { AssignStatus } from "@/enum/list.enum";
import { useAppSelector } from "@/redux/hooks";
import { useSearch } from "@/utils/useSearch";

import { useGetCampaignRoutingQuery } from "@/pages/Private/redux/campaignRouting/campaignRouting.api";
import { selectCampaignRoutingSettings } from "@/pages/Private/redux/campaignRouting/campaignRouting.slice";
import { useGetLemlistsQuery } from "@/pages/Private/redux/lemlist/lemlist.api";
import { useUpdateListPersonCampaignMutation } from "@/pages/Private/redux/list/list.api";

import { routingReasonText } from "../../components/CampaignRouting/utils/campaignRoutingReason";
import { RoutingTranslate } from "../../components/CampaignRouting/utils/campaignRoutingText";
import {
	CampaignAutoComplete,
	CampaignAutoCompleteItem,
} from "../../components/CampaignAutoComplete/CampaignAutoComplete";
import { ListPerson } from "../../schema/list";

type SelectCampaignAndProspect = (
	id: number | string,
	listPersonId: number,
	type?: "internal" | "lemlist"
) => void;

/**
 * Why the lead holds its campaign (PREC-4219, mockup frame 5): "Routed by rule 2: …", "Default
 * campaign (no rule matched)", or null for a campaign a person picked. The list's routing is read
 * only for a RULE lead (every row of a list shares the one cached request), to number the rule.
 */
export const useRoutingReason = (row: ListPerson): string | null => {
	const { t } = useTranslation();
	const listId = row.list?.id ?? 0;
	const needsRules = row.campaignAssignedBy === "RULE" && !!row.campaignId && !!listId;

	useGetCampaignRoutingQuery(listId, { skip: !needsRules });

	const settings = useAppSelector(selectCampaignRoutingSettings(listId));

	return routingReasonText(row, settings, t as RoutingTranslate);
};

const RoutingReasonLine: FunctionComponent<{ reason: string | null }> = ({ reason }) =>
	reason ? (
		<p
			className="max-w-[180px] truncate text-xs text-text-meta"
			data-testid="campaign-routing-reason"
			title={reason}
		>
			{reason}
		</p>
	) : null;

/** The lead's stored internal campaign as the picker's selection, or null when it holds none. */
const storedCampaignOf = (row: ListPerson): CampaignAutoCompleteItem | null => {
	if (!row?.campaignId) {
		return null;
	}

	const config = row?.list?.campaignConfig?.[row.campaignId];

	return { id: row.campaignId, title: config?.name ?? row.campaignId, type: "internal" };
};

/**
 * The campaign picker of a lead that is not yet in a campaign (draft or saved for later). It shows
 * the campaign the lead holds (preassigned by routing or picked by a person) and nothing else: it
 * no longer guesses one from the lead's language or the list's default (PREC-4219), which is the
 * routing's job on the server.
 */
const AutocompleteCampaignColumn: FunctionComponent<{
	handleSelectCampaignAndProspect: SelectCampaignAndProspect;
	row: ListPerson;
}> = ({ handleSelectCampaignAndProspect, row }) => {
	const { query: lemlistQuery, setSearchValue: setSearchLemlist } = useSearch();
	const [updateListPersonCampaign] = useUpdateListPersonCampaignMutation();
	const reason = useRoutingReason(row);

	const savedInternalCampaign = useMemo(() => storedCampaignOf(row), [row]);

	const savedLemlistCampaign = useMemo<CampaignAutoCompleteItem | undefined>(() => {
		if (!row?.lemlist?.id) {
			return undefined;
		}

		return {
			id: row.lemlist.id,
			title: row.lemlist.name ?? "",
			type: "lemlist",
		} as CampaignAutoCompleteItem;
	}, [row?.lemlist?.id, row?.lemlist?.name]);

	const [selectedInternalCampaign, setSelectedInternalCampaign] =
		useState<CampaignAutoCompleteItem | null>(savedInternalCampaign);

	const [selectedCampaign, setSelectedCampaign] = useState<CampaignAutoCompleteItem | undefined>(
		savedLemlistCampaign
	);

	// A refetched row resets the pick to what the server stored (React's "state from a prop
	// change", adjusted while rendering instead of in an effect).
	const [syncedFrom, setSyncedFrom] = useState({
		internal: savedInternalCampaign,
		lemlist: savedLemlistCampaign,
	});

	if (
		syncedFrom.internal !== savedInternalCampaign ||
		syncedFrom.lemlist !== savedLemlistCampaign
	) {
		setSyncedFrom({ internal: savedInternalCampaign, lemlist: savedLemlistCampaign });

		if (savedInternalCampaign) {
			setSelectedInternalCampaign(savedInternalCampaign);
			setSelectedCampaign(undefined);
		} else if (savedLemlistCampaign) {
			setSelectedCampaign(savedLemlistCampaign);
			setSelectedInternalCampaign(null);
		}
	}

	const handleSelectCampaign = (value?: CampaignAutoCompleteItem) => {
		if (value?.type === "internal") {
			setSelectedInternalCampaign(value);
			setSelectedCampaign(undefined);
		} else {
			setSelectedCampaign(value);
			setSelectedInternalCampaign(null);
		}

		if (value?.id) {
			handleSelectCampaignAndProspect(
				value.type === "internal" ? value.id : +value.id,
				row.id,
				value.type
			);

			updateListPersonCampaign({
				id: row.id,
				campaignId: value.type === "internal" ? (value.id as string) : null,
				lemlistId: value.type === "lemlist" ? +value.id : null,
			});
		}
	};

	const { data: lemlists } = useGetLemlistsQuery({
		page: 1,
		limit: 10,
		searchValue: lemlistQuery,
	});

	// The reason explains the stored campaign only: a fresh pick here is the person's own.
	const showsStoredCampaign =
		!!row.campaignId && selectedInternalCampaign?.id === row.campaignId && !selectedCampaign;

	return (
		<div>
			<CampaignAutoComplete
				data={[
					...(Object.keys(row?.list?.campaignConfig || {})
						.filter((c) => row?.list?.campaignConfig[c]?.isValid)
						.map((c) => {
							return {
								id: c,
								title: row?.list?.campaignConfig[c]?.name,
								type: "internal",
							} as CampaignAutoCompleteItem;
						}) || []),
					...(lemlists?.data?.map((c) => {
						return { id: c.id, title: c.name, type: "lemlist" } as CampaignAutoCompleteItem;
					}) || []),
				]}
				handleSearch={(value: string) => setSearchLemlist(value)}
				handleSelect={handleSelectCampaign}
				selectedItem={selectedInternalCampaign || selectedCampaign}
			/>
			<RoutingReasonLine reason={showsStoredCampaign ? reason : null} />
		</div>
	);
};

/** The campaign tag of a lead that is in a campaign already, with its routing reason. */
const CampaignTag: FunctionComponent<{
	row: ListPerson;
	name: string;
	handleCopyClick: (value: string) => void;
}> = ({ row, name, handleCopyClick }) => {
	const reason = useRoutingReason(row);

	return (
		<Tooltip content={reason ? `${name}. ${reason}` : name}>
			<div className="flex flex-col">
				<button
					className="text-sm text-ellipsis overflow-hidden max-w-[120px] cursor-pointer"
					onClick={() => handleCopyClick(name)}
				>
					{name}
				</button>
				<RoutingReasonLine reason={row.lemlist?.id ? null : reason} />
			</div>
		</Tooltip>
	);
};

export const renderAssignToColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	handleSelectCampaignAndProspect: SelectCampaignAndProspect
): TableColumn => {
	return {
		title: ts("assignTo"),
		minWidth: "180px",
		calcWidth: 180,
		width: "fit-content",
		orderTitle: "lemlist.name",
		orderStatus: 0,
		locked: "leftThird",
		icon: faLock,
		uid: "lemlist",
		hideable: true,
		moveable: false,
		show: false,
		render: (row: ListPerson) => {
			const currentCampaign =
				row?.campaignId && !row.lemlist?.id
					? row.campaignConfig
						? row.campaignConfig
						: { ...row.list.campaignConfig[row.campaignId], id: row?.campaignId }
					: undefined;

			const isEditable =
				row?.assignStatus === AssignStatus.DRAFT ||
				row?.assignStatus === AssignStatus.SAVED_FOR_LATER;

			const name =
				currentCampaign?.name ?? row?.lemlist?.name ?? row?.list.lemlists?.at(0)?.name ?? "";

			return (
				<div className="relative flex">
					{isEditable ? (
						<AutocompleteCampaignColumn
							handleSelectCampaignAndProspect={handleSelectCampaignAndProspect}
							row={row}
						/>
					) : row?.lemlist?.name || currentCampaign?.name ? (
						<CampaignTag handleCopyClick={handleCopyClick} name={name} row={row} />
					) : null}
				</div>
			);
		},
	};
};
